import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RightPaneVisibility } from '../contentPage/components/layout/RightPanel'

export const MAX_TEMPLATES_TO_ADD = 10

interface UnifiedFlowContentState {
  activeContentId?: string
  selectedElementId?: number | string
  areElementsDisabled: boolean
  rightPanelVisibility: RightPaneVisibility
  isLeftPanelVisible: boolean | undefined
  shouldShowUnsavedModal?: boolean
  openUnsavedModal?: boolean
  pendingSelection?: {
    elementId?: number | string
  }
}

interface UnifiedFlowState extends UnifiedFlowContentState {
  showTemplateView: boolean
  templatesToAdd: TemplateToAdd[]
}
export interface TemplateToAdd {
  id: string
  count: number
}

const contentInitialState: UnifiedFlowContentState = {
  selectedElementId: undefined,
  activeContentId: undefined,
  areElementsDisabled: false,
  rightPanelVisibility: 'auto',
  isLeftPanelVisible: undefined,
  shouldShowUnsavedModal: undefined,
  openUnsavedModal: undefined,
  pendingSelection: undefined,
}

const initialState: UnifiedFlowState = {
  showTemplateView: false,
  templatesToAdd: [],
  ...contentInitialState,
}

const unifiedFlowSlice = createSlice({
  name: 'unifiedFlow',
  initialState,
  reducers: {
    unifiedContentInitialize: (
      state,
      action: PayloadAction<Partial<UnifiedFlowState>>
    ) => {
      state.activeContentId = action.payload.activeContentId
      state.selectedElementId = action.payload.selectedElementId
    },
    selectElement: (
      state,
      action: PayloadAction<{
        elementId?: number | string
        shouldShowUnsavedModal?: boolean
      }>
    ) => {
      const { elementId, shouldShowUnsavedModal } = action.payload

      state.shouldShowUnsavedModal =
        shouldShowUnsavedModal ?? state.shouldShowUnsavedModal

      if (state.shouldShowUnsavedModal) {
        state.openUnsavedModal = true
        state.pendingSelection = { elementId }
      } else {
        state.selectedElementId = elementId
      }
    },
    userInputFormHasChanged: (state, action: PayloadAction<boolean>) => {
      // TODO: Decide what to do with this
      // const hasInputFormChanged = action.payload
      // if (hasInputFormChanged && !state.showComponentConfiguration) {
      //   state.areElementsDisabled = true
      // } else if (hasInputFormChanged && state.showComponentConfiguration) {
      //   state.shouldShowUnsavedModal = true
      // } else {
      //   state.shouldShowUnsavedModal = false
      //   state.areElementsDisabled = false
      // }
    },
    toggleOpenUnsavedModal: (state, action: PayloadAction<boolean>) => {
      state.openUnsavedModal = action.payload
    },
    inputFormLeave: (state) => {
      state.selectedElementId = state.pendingSelection?.elementId
      state.pendingSelection = undefined
      state.openUnsavedModal = false
      state.shouldShowUnsavedModal = false
    },
    toggleElementsDisabled: (state, action: PayloadAction<boolean>) => {
      state.areElementsDisabled = action.payload
    },
    resetContentPageState: (state) => {
      Object.keys(contentInitialState).forEach((key) => {
        state[key] = contentInitialState[key]
      })
    },
    showTemplates: (state) => {
      state.showTemplateView = true
    },
    hideTemplates: (state) => {
      state.showTemplateView = false
    },
    showRightPanel: (
      state,
      action: PayloadAction<RightPaneVisibility | undefined>
    ) => {
      state.rightPanelVisibility = action.payload || 'visible'
    },
    hideRightPanel: (
      state,
      action: PayloadAction<RightPaneVisibility | undefined>
    ) => {
      state.rightPanelVisibility = action.payload || 'hidden'
    },
    showLeftPanel: (state) => {
      state.isLeftPanelVisible = true
    },
    hideLeftPanel: (state) => {
      state.isLeftPanelVisible = false
    },
    cancelTemplatesAdd: (state) => {
      state.templatesToAdd = []
      state.showTemplateView = false
    },
    resetTemplatesToAdd: (state) => {
      state.templatesToAdd = []
    },
    postAddTemplate: (
      state,
      action: PayloadAction<Partial<UnifiedFlowState>>
    ) => {
      state.templatesToAdd = []
      state.showTemplateView = false
      state.selectedElementId = action.payload.selectedElementId
    },
    addTemplate(state, action: PayloadAction<{ templateId: string }>) {
      const templatesToAddCount = state.templatesToAdd.reduce(
        (acc, template) => acc + template.count,
        0
      )
      if (templatesToAddCount >= MAX_TEMPLATES_TO_ADD) {
        return
      }

      const templateId = action.payload.templateId
      const templateToAdd = state.templatesToAdd.find(
        (t) => t.id === templateId
      )
      if (templateToAdd) {
        templateToAdd.count++
      } else {
        state.templatesToAdd.push({ id: templateId, count: 1 })
      }
    },
    removeTemplate(
      state,
      action: PayloadAction<{ templateId: string; erase?: boolean }>
    ) {
      const templateId = action.payload.templateId
      const templateToRemove = state.templatesToAdd.find(
        (t) => t.id === templateId
      )
      if (templateToRemove) {
        if (action.payload.erase) {
          state.templatesToAdd = state.templatesToAdd.filter(
            (t) => t.id !== templateId
          )
        } else {
          templateToRemove.count--
          if (templateToRemove.count === 0) {
            state.templatesToAdd = state.templatesToAdd.filter(
              (t) => t.id !== templateId
            )
          }
        }
      }
    },
  },
})

export const {
  unifiedContentInitialize,
  selectElement,
  resetContentPageState,
  userInputFormHasChanged,
  toggleElementsDisabled,
  showTemplates,
  hideTemplates,
  showRightPanel,
  hideRightPanel,
  showLeftPanel,
  hideLeftPanel,
  addTemplate,
  postAddTemplate,
  removeTemplate,
  cancelTemplatesAdd,
  resetTemplatesToAdd,
  toggleOpenUnsavedModal,
  inputFormLeave,
} = unifiedFlowSlice.actions
export default unifiedFlowSlice.reducer
